/**
 * What the radar knows (Stage 88).
 *
 * The map in the corner has drawn two things since the first stage: the dummies, and the file at the
 * middle of it. In THE WAKE — the mode the game is named for — it has never drawn the nodes. The
 * strip at the top says who holds each of the eight and says nothing about where they are, so a
 * player crossing the yard with a node coming free has no way to know which way to run.
 *
 * This is the placement rule, pure so it is unit-tested: world positions into the map's own frame,
 * which is rotated so that forward is up, and anything past the edge pinned to the rim rather than
 * dropped — a node you cannot see is exactly the one you need pointing at.
 */

export interface RadarNode {
  id: number;
  label: string;
  pos: { x: number; z: number };
  owner: number;
  contested: boolean;
  puller: number;
}

export interface RadarMark {
  id: number;
  label: string;
  /** canvas pixels */
  x: number;
  y: number;
  /** true when the node is off the map and this mark is pinned to the rim */
  edge: boolean;
  /** how far away it is, in metres */
  distance: number;
  owner: number;
  contested: boolean;
  puller: number;
}

/** the colours the map draws a node in: VANTAGE's violet, the two cells, and amber for a contest */
export const NODE_COLOURS = { vantage: "#8f4dff", one: "#37ff8b", two: "#35f2ff", contested: "#ffb02e" } as const;

export function nodeColour(n: { owner: number; contested: boolean }): string {
  if (n.contested) return NODE_COLOURS.contested;
  return n.owner === 1 ? NODE_COLOURS.one : n.owner === 2 ? NODE_COLOURS.two : NODE_COLOURS.vantage;
}

/**
 * A world offset in the map's own frame. The map turns under the file so that forward is up, which
 * means the file's own basis: `yawRight` across and `viewDir` up the screen. It had been turning the
 * other way since the first stage — a rotation by +yaw where the file's basis is −yaw — so at any
 * heading but due north the marks were mirrored through the forward axis, and facing west put what
 * was behind you at the top of the map (Stage 88).
 */
export function toMap(dx: number, dz: number, yaw: number, scale: number, w: number, h: number): { x: number; y: number } {
  const sin = Math.sin(yaw);
  const cos = Math.cos(yaw);
  const right = dx * cos - dz * sin; // d · yawRight
  const fwd = -dx * sin - dz * cos; // d · viewDir
  return { x: w / 2 + right * scale, y: h / 2 - fwd * scale };
}

/**
 * Where each node goes on the map. `scale` is pixels per metre and the frame is the player's: the
 * map turns under them, so forward is always up. A node further out than the map reaches is pinned
 * to the rim on the same bearing, and says so, so the caller can draw it smaller.
 */
export function nodeMarks(nodes: readonly RadarNode[], at: { x: number; z: number }, yaw: number, scale: number, w: number, h: number, pad = 3): RadarMark[] {
  const out: RadarMark[] = [];
  for (const n of nodes) {
    const m = place(n.pos, at, yaw, scale, w, h, pad);
    out.push({ id: n.id, label: n.label, x: m.x, y: m.y, edge: m.edge, distance: m.distance, owner: n.owner, contested: n.contested, puller: n.puller });
  }
  return out;
}

/**
 * A world point on the map, pinned to the rim when it is past the edge — along its own bearing
 * rather than clamped per axis, which would slide a mark round the corner of the map and point at a
 * street the thing is not on.
 */
export function place(pos: { x: number; z: number }, at: { x: number; z: number }, yaw: number, scale: number, w: number, h: number, pad = 3): { x: number; y: number; edge: boolean; distance: number } {
  const cx = w / 2;
  const cy = h / 2;
  const dx = pos.x - at.x;
  const dz = pos.z - at.z;
  const m = toMap(dx, dz, yaw, scale, w, h);
  let px = m.x;
  let py = m.y;
  let edge = false;
  if (px < pad || px > w - pad || py < pad || py > h - pad) {
    edge = true;
    const vx = px - cx;
    const vy = py - cy;
    const len = Math.hypot(vx, vy) || 1;
    const limit = Math.min((cx - pad) / (Math.abs(vx) / len || 1e-6), (cy - pad) / (Math.abs(vy) / len || 1e-6));
    px = cx + (vx / len) * limit;
    py = cy + (vy / len) * limit;
  }
  return { x: px, y: py, edge, distance: Math.hypot(dx, dz) };
}

/**
 * Where the contract wants you (Stage 92).
 *
 * The campaign has put a marker in the world since Stage 10 — the place to reach, the body to
 * escort, the things to destroy — and the map in the corner has never drawn any of it. A contract
 * that says REACH THE SUBSTATION and leaves the player turning on the spot to find which way that
 * is has told them nothing. These go through the same placement as the nodes, so an objective past
 * the edge of the map is pinned to the rim on its own bearing: the one you cannot see is the one
 * you most need pointing at.
 */
export type SpotKind = "goal" | "escort" | "target";

export interface RadarSpot {
  kind: SpotKind;
  x: number;
  z: number;
}

export interface SpotMark {
  kind: SpotKind;
  /** canvas pixels */
  x: number;
  y: number;
  /** true when it is off the map and this mark is pinned to the rim */
  edge: boolean;
  /** how far away it is, in metres */
  distance: number;
}

/** the colours the map draws a contract in: the goal in the terminal's cyan, the escort amber, a target magenta */
export const SPOT_COLOURS: Record<SpotKind, string> = { goal: "#35f2ff", escort: "#ffb02e", target: "#ff3ec9" };

export function spotMarks(spots: readonly RadarSpot[], at: { x: number; z: number }, yaw: number, scale: number, w: number, h: number, pad = 3): SpotMark[] {
  return spots.map((s) => {
    const m = place(s, at, yaw, scale, w, h, pad);
    return { kind: s.kind, x: m.x, y: m.y, edge: m.edge, distance: m.distance };
  });
}

/**
 * The city's doors on the map (Stage 704).
 *
 * In the city every street gate is a door to the next district, and the map in the corner drew
 * none of them: the only thing that said a gate led anywhere was the HUD line, and only once the
 * file was already standing in it. Each gate is marked where it is, in its destination's cast
 * colour, with the destination's initials — pinned to the rim on its own bearing when it is past
 * the edge, like everything else the map draws, so the way to DEADLETTER DOCKS is on the map from
 * anywhere in LEASE ROW.
 */
export interface RadarGate {
  x: number;
  z: number;
  /** the destination's initials: DD for DEADLETTER DOCKS */
  label: string;
  colour: string;
}

export interface GateMark {
  label: string;
  colour: string;
  /** canvas pixels */
  x: number;
  y: number;
  edge: boolean;
  distance: number;
}

/** the casts' colours, as the map draws them */
export const CAST_HEX = { magenta: "#ff3ec9", cyan: "#35f2ff", amber: "#ffb02e" } as const;

/** a district's initials for the map: the first letter of each word of its name */
export function gateInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter((w) => /^[A-Z0-9]/i.test(w))
    .map((w) => w[0]!.toUpperCase())
    .join("")
    .slice(0, 3);
}

/** the map's gates from the city's (shared/net/citygates.ts `gateSigns`) */
export function radarGates(gates: readonly { x: number; z: number; name: string; cast: keyof typeof CAST_HEX }[]): RadarGate[] {
  return gates.map((g) => ({ x: g.x, z: g.z, label: gateInitials(g.name), colour: CAST_HEX[g.cast] }));
}

export function gateMarks(gates: readonly RadarGate[], at: { x: number; z: number }, yaw: number, scale: number, w: number, h: number, pad = 3): GateMark[] {
  return gates.map((g) => {
    const m = place(g, at, yaw, scale, w, h, pad);
    return { label: g.label, colour: g.colour, x: m.x, y: m.y, edge: m.edge, distance: m.distance };
  });
}

/**
 * The map's footer (Stage 129). It had said "tap to walk" since the first HUD, on every platform,
 * and nothing anywhere handled a tap or a click on the map. What the map is: heading-up — the
 * file's view is up the screen — and this many metres across, from the same scale it is drawn at.
 */
export function mapFooter(acrossMetres: number, compact = false): string {
  // the phone's map box is 78 px wide at 8 px type (Stage 132): the arrow alone says heading-up
  return mapFootText(acrossMetres, compact ? "wide" : "full");
}

/**
 * The footer's forms, longest first (Stage 160).
 *
 * The full form has never fitted the box it is drawn in. Measured on the drawn HUD, the map's
 * footer wants 123 px in a 116 px box with a two-digit distance and 129 with a three — every
 * district, every window width, since the footer was written. `▲ AHEAD · 114 M ACROS` is what the
 * campaign probe's own frame shows. The text was checked, exactly, and the fit never was.
 *
 * So it sheds, in the order a player can afford to lose it: the heading-up word, which the arrow
 * already says; then `ACROSS` for the phone's shorter `WIDE`; then the unit alone. The arrow and
 * the number are never shed — they are what the footer is for.
 */
export const MAP_FOOT_FORMS = ["full", "no-ahead", "wide", "metres"] as const;
export type MapFootForm = (typeof MAP_FOOT_FORMS)[number];

export function mapFootText(acrossMetres: number, form: MapFootForm): string {
  const m = Math.round(acrossMetres);
  if (form === "full") return `▲ AHEAD · ${m} M ACROSS`;
  if (form === "no-ahead") return `▲ ${m} M ACROSS`;
  if (form === "wide") return `▲ ${m} M WIDE`;
  return `▲ ${m} M`;
}

/** The longest form that fits the box, given what each form measured. The last form is the floor. */
export function mapFoot(box: number, widths: readonly number[]): MapFootForm {
  for (let i = 0; i < MAP_FOOT_FORMS.length; i++) {
    if ((widths[i] ?? Infinity) <= box) return MAP_FOOT_FORMS[i]!;
  }
  return MAP_FOOT_FORMS[MAP_FOOT_FORMS.length - 1]!;
}
